/**
 * Pointer-driven drag for rail tiles, with the spring physics from spring.ts.
 *
 * ── Why not HTML5 drag-and-drop ──────────────────────────────────────────
 * The browser owns the drag image under DnD: it is a static bitmap you cannot
 * move, tilt or spring. Everything the gesture is for — the tile trailing the
 * pointer, swinging with its velocity, landing with a bounce — needs the
 * element to be ours every frame. The TaskBar's Drive/Workspace buttons keep
 * their HTML5 handlers for FILE drops from the drive; that is a different
 * event family (dragenter/drop) and cannot collide with pointer events.
 *
 * ── The state machine ────────────────────────────────────────────────────
 *   idle ─pointerdown→ pending ─moved > threshold→ dragging ─pointerup→ settling ─spring done→ idle
 *                         └─pointerup (a click)→ idle
 *
 * `pending` is what lets a tile still be clicked and long-pressed: the drag
 * only claims the gesture past the same 10px the hold-menu already cancels
 * on, so click / hold / drag never fight over one press.
 *
 * ── Two kinds of target ──────────────────────────────────────────────────
 * The caller's `resolveTarget` answers, for the pointer's position, either a
 * COMBINE target (a tile the item may be dropped INTO — app on app makes a
 * folder, app on folder files it) or a GAP (a slot BETWEEN tiles, where the
 * item would land to be reordered). A combine target must be ARMED — the
 * pointer resting on it for `armDelayMs` — before it takes a drop, so passing
 * over a neighbour on the way somewhere merges nothing; a gap needs no rest,
 * it is where the item is right now, and the caller shows it by shifting
 * the tiles and drawing a marker. At release an armed combine wins over the
 * gap.
 *
 * ── Per-frame work stays off React ───────────────────────────────────────
 * The ghost's transform is written straight to its DOM node from a
 * requestAnimationFrame loop. React state changes only at the edges: a drag
 * starting (dim the source), the armed target or the gap changing (ring a
 * tile, shift the others), and the settle finishing (unmount the ghost).
 *
 * ── Auto-scroll ──────────────────────────────────────────────────────────
 * The well scrolls, so a long rail cannot be reordered end to end without it
 * moving under the drag. While dragging, the loop nudges the caller's scroll
 * container whenever the pointer is within `AUTOSCROLL_EDGE` of its top or
 * bottom edge — the pointer stays put and the list comes to it.
 *
 * Touch is deliberately not a drag surface here: a vertical touch-drag on the
 * well must keep scrolling it. Touch users reach the same operations through
 * the context menu.
 */
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { createElement } from 'react'
import { useTheme, getThemeClass } from '../ThemeProvider.js'
import {
  RAIL_FOLLOW_SPRING, RAIL_SETTLE_SPRING, stepSpring, isSettled, swingAngle, prefersReducedMotion, type SpringState,
} from './spring.js'

export type RailDragItem =
  | {
      type: 'app'
      kind: 'miniapp' | 'html'
      ref: string
      /** The folder the tile is being dragged OUT of (a popup row), or null for a rail tile. */
      fromFolderId: string | null
    }
  | { type: 'folder'; id: string }

/** A drop INTO a tile. `key` is the tile's rail key; `el` is its element
 *  (for the ring and the fly-in). */
export interface CombineTarget { kind: 'combine'; key: string; el: Element }
/** A drop BETWEEN items: `index` is the slot in the list WITHOUT the dragged
 *  item, `rect` where an item in that slot sits (for the fly-in), and `scope`
 *  WHICH list — the rail, or a folder's member list in its popup. */
export interface GapTarget { kind: 'gap'; scope: string; index: number; rect: { left: number; top: number } }
export type RailDropTarget = CombineTarget | GapTarget

export interface UseRailDragOptions {
  /** What is under the pointer, for this item: a combine target, a gap, or
   *  nothing. The caller owns the tiles' geometry and data attributes. */
  resolveTarget: (el: Element | null, item: RailDragItem, point: { x: number; y: number }) => RailDropTarget | null
  /** A drop on an ARMED combine target or the current gap. Return true to
   *  let the ghost fly into the target; false (or nothing) sends it home. */
  onDrop: (item: RailDragItem, target: RailDropTarget) => boolean | void
  /** The element to auto-scroll while dragging near its edges (the well). */
  scrollContainer?: () => HTMLElement | null
  /** Movement before a press becomes a drag. Default 10px. */
  thresholdPx?: number
  /** How long the pointer must rest on a combine target before it is armed. Default 120ms. */
  armDelayMs?: number
}

export interface RailDragApi {
  /** Attach to a tile's `onPointerDown`. `renderGhost` produces the ghost's
   *  content (the same glyph the tile shows). */
  onPointerDown: (e: ReactPointerEvent<HTMLElement>, item: RailDragItem, renderGhost: () => ReactNode) => void
  /** The long-press menu opened on this press: it is no longer a drag candidate. */
  cancelPending: () => void
  /** Abandon whatever is in flight (an identity switch pulled the rail away). */
  cancel: () => void
  /** The list moved under a still pointer (the well scrolled): look again. */
  reresolve: () => void
  /** Call at the top of a tile's click handler: returns true — once — when the
   *  press that just ended was a drag, so the click must be ignored. */
  consumeClick: () => boolean
  /** The item in flight, for dimming its source tile. Null when idle. */
  dragging: RailDragItem | null
  /** True once the pointer is up and the ghost is flying to its destination.
   *  The caller HIDES the source rather than dimming it for this stretch —
   *  see the note on `settle`. */
  settling: boolean
  /** The armed combine target's key, for the ring on its tile. Null when none. */
  armedKey: string | null
  /** The gap the item would land in — which list, and where — for shifting
   *  that list's items and drawing the marker. Null when none. */
  gap: { scope: string; index: number } | null
  /** The ghost — render it once, anywhere in the tree. */
  ghost: ReactNode
}

const GHOST_SIZE = 40
const DRAG_SCALE = 1.08
const DROP_SCALE = 0.55
const AUTOSCROLL_EDGE = 28
const AUTOSCROLL_PX_PER_S = 420

interface Press {
  item: RailDragItem
  renderGhost: () => ReactNode
  startX: number
  startY: number
  /** Pointer offset from the tile's top-left, so the grab point stays under the cursor. */
  grabDx: number
  grabDy: number
  /** Where the tile was, for springing home. */
  homeX: number
  homeY: number
  pointerId: number
}

const targetKey = (t: RailDropTarget) => (t.kind === 'combine' ? `combine:${t.key}` : `gap:${t.scope}:${t.index}`)

export function useRailDrag(opts: UseRailDragOptions): RailDragApi {
  const { resolveTarget, onDrop, scrollContainer, thresholdPx = 10, armDelayMs = 120 } = opts
  const optsRef = useRef({ resolveTarget, onDrop, scrollContainer })
  optsRef.current = { resolveTarget, onDrop, scrollContainer }

  const [dragging, setDragging] = useState<RailDragItem | null>(null)
  const [settling, setSettling] = useState(false)
  const [armedKey, setArmedKey] = useState<string | null>(null)
  const [gapState, setGapState] = useState<{ scope: string; index: number } | null>(null)
  const [ghostContent, setGhostContent] = useState<ReactNode>(null)

  const press = useRef<Press | null>(null)
  const phase = useRef<'idle' | 'pending' | 'dragging' | 'settling'>('idle')
  const ghostEl = useRef<HTMLDivElement | null>(null)
  const clickConsumed = useRef(false)
  const reduced = useRef(false)

  // Physics state, all refs: read and written by the rAF loop only.
  const pointer = useRef({ x: 0, y: 0 })
  const sx = useRef<SpringState>({ value: 0, velocity: 0 })
  const sy = useRef<SpringState>({ value: 0, velocity: 0 })
  const scale = useRef(1)
  const settleTo = useRef<{ x: number; y: number; scale: number; fade: boolean } | null>(null)
  const raf = useRef<number | null>(null)
  const lastT = useRef(0)

  // Targeting. `hover` is what is under the pointer right now; `armed` is the
  // combine target that has earned a drop; `gap` the current slot.
  const hover = useRef<{ key: string; target: RailDropTarget } | null>(null)
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const armed = useRef<CombineTarget | null>(null)
  const gap = useRef<GapTarget | null>(null)

  const paint = useCallback(() => {
    const el = ghostEl.current
    if (!el) return
    const angle = reduced.current ? 0 : swingAngle(sx.current.velocity)
    el.style.transform = `translate3d(${sx.current.value}px, ${sy.current.value}px, 0) rotate(${angle}deg) scale(${scale.current})`
  }, [])

  const stopLoop = useCallback(() => {
    if (raf.current !== null) cancelAnimationFrame(raf.current)
    raf.current = null
  }, [])

  const finish = useCallback(() => {
    stopLoop()
    phase.current = 'idle'
    setSettling(false)
    press.current = null
    settleTo.current = null
    armed.current = null
    gap.current = null
    hover.current = null
    setDragging(null)
    setArmedKey(null)
    setGapState(null)
    setGhostContent(null)
    // Only if THIS hook set them. `finish()` also runs on cancel paths, and
    // stripping unconditionally let a rail settle clear the `grabbing` /
    // `user-select: none` a live tab drag was relying on.
    if (ownsBodyStyles.current) {
      ownsBodyStyles.current = false
      document.body.style.removeProperty('cursor')
      document.body.style.removeProperty('user-select')
    }
  }, [stopLoop])

  /** Re-evaluate the target from the current pointer position. Called on
   *  every move and after every auto-scroll step (the list moved under a
   *  still pointer). */
  const updateHover = useCallback(() => {
    const p = press.current
    if (!p || phase.current !== 'dragging') return
    const { x, y } = pointer.current
    const el = document.elementFromPoint(x, y)
    const found = optsRef.current.resolveTarget(el, p.item, { x, y })
    const key = found ? targetKey(found) : null
    if (ghostEl.current) ghostEl.current.dataset.hover = key ?? ''
    if (key === (hover.current?.key ?? null)) return
    if (armTimer.current) { clearTimeout(armTimer.current); armTimer.current = null }
    hover.current = found ? { key: key!, target: found } : null
    // Leaving an armed tile disarms it; a gap is never armed, it just IS.
    if (armed.current && key !== `combine:${armed.current.key}`) {
      armed.current = null
      if (ghostEl.current) delete ghostEl.current.dataset.armed
      setArmedKey(null)
    }
    const nextGap = found?.kind === 'gap' ? found : null
    if (nextGap?.index !== gap.current?.index || nextGap?.scope !== gap.current?.scope) {
      gap.current = nextGap
      setGapState(nextGap ? { scope: nextGap.scope, index: nextGap.index } : null)
    }
    if (found?.kind === 'combine') {
      const target = found
      armTimer.current = setTimeout(() => {
        armTimer.current = null
        if (phase.current !== 'dragging' || hover.current?.key !== key) return
        armed.current = target
        if (ghostEl.current) ghostEl.current.dataset.armed = key!
        setArmedKey(target.key)
      }, armDelayMs)
    }
  }, [armDelayMs])

  const frame = useCallback((t: number) => {
    const dt = lastT.current ? (t - lastT.current) / 1000 : 1 / 60
    lastT.current = t
    const p = press.current
    if (!p) return
    if (phase.current === 'dragging') {
      const tx = pointer.current.x - p.grabDx
      const ty = pointer.current.y - p.grabDy
      if (reduced.current) {
        sx.current = { value: tx, velocity: 0 }
        sy.current = { value: ty, velocity: 0 }
      } else {
        sx.current = stepSpring(sx.current, tx, RAIL_FOLLOW_SPRING, dt)
        sy.current = stepSpring(sy.current, ty, RAIL_FOLLOW_SPRING, dt)
      }
      scale.current += (DRAG_SCALE - scale.current) * Math.min(1, dt * 12)
      paint()
      // Auto-scroll: the pointer near the well's edge pulls the list toward it.
      const box = optsRef.current.scrollContainer?.()
      if (box) {
        const r = box.getBoundingClientRect()
        const y = pointer.current.y
        let dir = 0
        if (y < r.top + AUTOSCROLL_EDGE && y > r.top - AUTOSCROLL_EDGE) dir = -1
        else if (y > r.bottom - AUTOSCROLL_EDGE && y < r.bottom + AUTOSCROLL_EDGE) dir = 1
        if (dir !== 0) {
          const before = box.scrollTop
          box.scrollTop = before + dir * AUTOSCROLL_PX_PER_S * dt
          if (box.scrollTop !== before) updateHover()
        }
      }
      raf.current = requestAnimationFrame(frame)
      return
    }
    if (phase.current === 'settling' && settleTo.current) {
      const dest = settleTo.current
      sx.current = stepSpring(sx.current, dest.x, RAIL_SETTLE_SPRING, dt)
      sy.current = stepSpring(sy.current, dest.y, RAIL_SETTLE_SPRING, dt)
      scale.current += (dest.scale - scale.current) * Math.min(1, dt * 10)
      if (ghostEl.current && dest.fade) {
        const remaining = Math.hypot(sx.current.value - dest.x, sy.current.value - dest.y)
        ghostEl.current.style.opacity = String(Math.max(0, Math.min(1, remaining / 60)))
      }
      paint()
      if (isSettled(sx.current, dest.x, 0.5, 5) && isSettled(sy.current, dest.y, 0.5, 5)) finish()
      else raf.current = requestAnimationFrame(frame)
    }
  }, [paint, finish, updateHover])

  const startLoop = useCallback(() => {
    stopLoop()
    lastT.current = 0
    raf.current = requestAnimationFrame(frame)
  }, [frame, stopLoop])

  const settle = useCallback((dest: { x: number; y: number; scale: number; fade: boolean }) => {
    phase.current = 'settling'
    setSettling(true)
    if (armTimer.current) { clearTimeout(armTimer.current); armTimer.current = null }
    // The shift and the ring belong to the drag, not the landing: clear them
    // now so the tiles settle into their real slots while the ghost flies.
    gap.current = null
    setGapState(null)
    armed.current = null
    setArmedKey(null)
    if (reduced.current) { finish(); return }
    settleTo.current = dest
    startLoop()
  }, [finish, startLoop])

  const cancelDrag = useCallback(() => {
    const p = press.current
    if (!p) return
    setTimeout(() => { clickConsumed.current = false }, 0)
    if (phase.current === 'dragging') settle({ x: p.homeX, y: p.homeY, scale: 1, fade: false })
    else finish()
  }, [finish, settle])

  useEffect(() => {
    // Window-level listeners for the life of a press: the pointer leaves the
    // tile the moment a drag starts, and pointer capture would steal the
    // hold-menu's own leave/cancel handling.
    const onMove = (e: PointerEvent) => {
      const p = press.current
      if (!p || e.pointerId !== p.pointerId) return
      pointer.current = { x: e.clientX, y: e.clientY }
      if (phase.current === 'pending') {
        const dx = e.clientX - p.startX
        const dy = e.clientY - p.startY
        if (dx * dx + dy * dy < thresholdPx * thresholdPx) return
        phase.current = 'dragging'
        clickConsumed.current = true
        reduced.current = prefersReducedMotion()
        sx.current = { value: p.homeX, velocity: 0 }
        sy.current = { value: p.homeY, velocity: 0 }
        scale.current = 1
        ownsBodyStyles.current = true
        document.body.style.cursor = 'grabbing'
        document.body.style.userSelect = 'none'
        setDragging(p.item)
        setGhostContent(p.renderGhost())
        startLoop()
      }
      if (phase.current === 'dragging') updateHover()
    }
    const onUp = (e: PointerEvent) => {
      const p = press.current
      if (!p || e.pointerId !== p.pointerId) return
      if (phase.current === 'pending') { press.current = null; phase.current = 'idle'; return }   // a click; the tile handles it
      if (phase.current !== 'dragging') return
      // The click for this press — if the browser fires one at all — is
      // dispatched synchronously after this pointerup, so a macrotask later
      // the flag has done its job and must not survive to swallow the NEXT
      // click on a tile that never presses through this hook (a folder).
      setTimeout(() => { clickConsumed.current = false }, 0)
      // An armed combine wins over the gap the pointer happens to be in.
      const target: RailDropTarget | null = armed.current ?? gap.current
      // A combine target whose tile left the DOM mid-drag (deleted elsewhere)
      // has a zero rect; do not fly the ghost to the page's corner over it.
      if (target && target.kind === 'combine' && !target.el.isConnected) {
        settle({ x: p.homeX, y: p.homeY, scale: 1, fade: false })
        return
      }
      if (target && optsRef.current.onDrop(p.item, target) === true) {
        if (target.kind === 'combine') {
          // Absorbed INTO a tile: shrink and fade, because nothing of the item
          // remains on the rail to hand over to.
          const r = target.el.getBoundingClientRect()
          settle({ x: r.left + r.width / 2 - GHOST_SIZE / 2, y: r.top + r.height / 2 - GHOST_SIZE / 2, scale: DROP_SCALE, fade: true })
        } else {
          // A reorder HANDS OVER to the real tile, which the drop has just put
          // in this exact slot: the ghost must arrive at full opacity so the
          // swap at `finish` is invisible. Fading it out instead left the slot
          // holding only the dimmed source tile for the last frames and then
          // popping it to full — the blink on every release.
          settle({ x: target.rect.left, y: target.rect.top, scale: 1, fade: false })
        }
      } else if (target) {
        settle({ x: sx.current.value, y: sy.current.value, scale: DROP_SCALE, fade: true })
      } else {
        settle({ x: p.homeX, y: p.homeY, scale: 1, fade: false })
      }
    }
    const onCancel = (e: PointerEvent) => {
      const p = press.current
      if (!p || e.pointerId !== p.pointerId) return
      cancelDrag()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase.current === 'dragging') cancelDrag()
    }
    // The pointer released outside the window (or the tab hidden) delivers no
    // pointerup; a drag left in flight would follow the next entering pointer.
    const onBlur = () => { if (phase.current === 'dragging' || phase.current === 'pending') cancelDrag() }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onCancel)
    window.addEventListener('keydown', onKey)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onCancel)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('blur', onBlur)
      stopLoop()
      if (armTimer.current) clearTimeout(armTimer.current)
    }
  }, [thresholdPx, startLoop, stopLoop, updateHover, settle, cancelDrag])

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>, item: RailDragItem, renderGhost: () => ReactNode) => {
    if (e.button !== 0 || e.pointerType === 'touch') return
    if (phase.current !== 'idle') return
    clickConsumed.current = false
    const r = e.currentTarget.getBoundingClientRect()
    press.current = {
      item, renderGhost,
      startX: e.clientX, startY: e.clientY,
      grabDx: e.clientX - r.left, grabDy: e.clientY - r.top,
      homeX: r.left, homeY: r.top,
      pointerId: e.pointerId,
    }
    pointer.current = { x: e.clientX, y: e.clientY }
    phase.current = 'pending'
  }, [])

  const cancelPending = useCallback(() => {
    if (phase.current === 'pending') { phase.current = 'idle'; press.current = null }
  }, [])

  // The ghost is PORTALED to <body>, outside every ThemedArea, so the themed-*
  // classes its content uses (FolderGlyph's `themed-bg-tertiary` / `themed-icon`)
  // would resolve against CSS variables that are not defined there — and a
  // COLOURED folder is worse than unstyled: `color-mix(..., var(--bg-tertiary))`
  // with an undefined var invalidates the whole declaration. Both sibling
  // portals in TaskBar apply the same class for the same reason.
  const ownsBodyStyles = useRef(false)
  const { settings } = useTheme()
  const ghostThemeClass = getThemeClass(settings.theme.workspace)

  // Whether this hook is the one currently holding the <body> drag styles.
  const consumeClick = useCallback(() => {
    if (!clickConsumed.current) return false
    clickConsumed.current = false
    return true
  }, [])

  const ghost = ghostContent
    ? createPortal(
        createElement('div', {
          ref: ghostEl,
          'aria-hidden': true,
          'data-rail-drag-ghost': true,
          className: ghostThemeClass,
          style: {
            position: 'fixed', left: 0, top: 0, width: GHOST_SIZE, height: GHOST_SIZE,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none', zIndex: 60, willChange: 'transform',
            // Held from near the top, so it swings like a hanging card.
            transformOrigin: '50% 30%',
          },
        }, ghostContent),
        document.body,
      )
    : null

  return { onPointerDown, cancelPending, cancel: cancelDrag, reresolve: updateHover, consumeClick, dragging, settling, armedKey, gap: gapState, ghost }
}
